import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

// A small in-memory undo stack.  Each entry carries a one-shot `undo` thunk
// captured at the time the action was performed (so it can replay against
// pre-mutation state).  We don't persist across reloads — undo is a "current
// session" affordance, not a history database.
//
// Stack is bounded to keep memory in check; oldest entries fall off when
// new ones are pushed.

export type UndoEntry = {
  label: string;
  undo: () => Promise<void> | void;
  /** Show a transient "label — Undo" toast when pushed.  Use for
   *  destructive actions (delete/archive) where the header button alone
   *  is easy to miss. */
  announce?: boolean;
};

const MAX_STACK = 50;

type Ctx = {
  push: (entry: UndoEntry) => void;
  popAndRun: () => Promise<void>;
  top: UndoEntry | null;
  size: number;
  /** Set when the most recent undo attempt threw; cleared automatically. */
  lastError: string | null;
};

const UndoCtx = createContext<Ctx | null>(null);

export function UndoProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<UndoEntry[]>([]);
  const [lastError, setLastError] = useState<string | null>(null);
  // Monotonic key so a second announcement restarts the toast timer.
  const [announced, setAnnounced] = useState<{
    label: string;
    key: number;
  } | null>(null);

  const push = useCallback((entry: UndoEntry) => {
    setStack((prev) => {
      const next = [...prev, entry];
      if (next.length > MAX_STACK) next.shift();
      return next;
    });
    if (entry.announce) {
      setAnnounced((prev) => ({ label: entry.label, key: (prev?.key ?? 0) + 1 }));
    }
  }, []);

  const popAndRun = useCallback(async () => {
    let entry: UndoEntry | undefined;
    setStack((prev) => {
      if (prev.length === 0) return prev;
      entry = prev[prev.length - 1];
      return prev.slice(0, -1);
    });
    // Wait a microtask so the setState above has been committed before we
    // call the thunk (so any reads of `top` reflect the pop).
    await Promise.resolve();
    if (!entry) return;
    try {
      await entry.undo();
      setLastError(null);
    } catch (err) {
      // The entry is already popped — replaying a thunk that just failed
      // would likely fail again, so drop it and tell the user instead of
      // failing silently (the button otherwise looks like it worked).
      console.error(`[undo] "${entry.label}" failed`, err);
      setLastError(entry.label);
    }
  }, []);

  const top = stack.length > 0 ? stack[stack.length - 1]! : null;

  return (
    <UndoCtx.Provider
      value={{ push, popAndRun, top, size: stack.length, lastError }}
    >
      {children}
      {lastError !== null && (
        <UndoErrorToast label={lastError} onDone={() => setLastError(null)} />
      )}
      {announced !== null && lastError === null && (
        <UndoAnnounceToast
          key={announced.key}
          label={announced.label}
          onUndo={() => {
            setAnnounced(null);
            void popAndRun();
          }}
          onDone={() => setAnnounced(null)}
        />
      )}
    </UndoCtx.Provider>
  );
}

function UndoAnnounceToast({
  label,
  onUndo,
  onDone,
}: {
  label: string;
  onUndo: () => void;
  onDone: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDone, 6000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      role="status"
      className="safe-inset fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-md bg-slate-900 px-3 py-2 text-sm text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
    >
      <span>{label}</span>
      <button
        type="button"
        onClick={onUndo}
        className="rounded px-1.5 py-0.5 font-medium text-sky-300 hover:bg-slate-700 hover:text-sky-200 dark:text-sky-700 dark:hover:bg-slate-300 dark:hover:text-sky-800"
      >
        Undo
      </button>
      <button
        type="button"
        onClick={onDone}
        aria-label="Dismiss"
        className="rounded px-1 text-slate-400 hover:bg-slate-700 hover:text-white dark:text-slate-500 dark:hover:bg-slate-300 dark:hover:text-slate-900"
      >
        ✕
      </button>
    </div>
  );
}

function UndoErrorToast({
  label,
  onDone,
}: {
  label: string;
  onDone: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDone, 5000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      role="alert"
      className="safe-inset fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-sm text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
    >
      Couldn’t undo “{label}”
    </div>
  );
}

export function useUndo(): Ctx {
  const ctx = useContext(UndoCtx);
  if (!ctx) throw new Error("useUndo must be used inside UndoProvider");
  return ctx;
}

// ---------------------------------------------------------------------------
// Header button
// ---------------------------------------------------------------------------

export function UndoButton() {
  const { top, popAndRun, size } = useUndo();
  const enabled = size > 0;
  return (
    <button
      type="button"
      onClick={() => void popAndRun()}
      disabled={!enabled}
      title={top ? `Undo: ${top.label}` : "Nothing to undo"}
      className={`rounded-md border px-2 py-1 text-xs font-medium ${
        enabled
          ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500"
      }`}
    >
      ↶ Undo
      {size > 1 && (
        <span className="ml-1 text-[10px] text-slate-400 dark:text-slate-500">({size})</span>
      )}
    </button>
  );
}
